import useCourseTheme from '../../core/hooks/useCourseTheme';
import { useCallback, useEffect, useState } from 'react';
import Joyride, { ACTIONS, CallBackProps, EVENTS, STATUS, Step } from 'react-joyride';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ExploreOutlinedIcon from '@mui/icons-material/ExploreOutlined';
import QuizOutlinedIcon from '@mui/icons-material/QuizOutlined';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import mixpanel from 'mixpanel-browser';
import { useCourseCore } from '../../core/modules/course';
import { useMeCore } from '../../core/modules/me';

const SEEN_STORAGE_KEY = 'platformTutorialSeen';
const AUTO_START_DELAY_MS = 1000;

type TourStep = Step & { name: string };

const TOUR_STEPS: TourStep[] = [
  {
    name: 'menu',
    target: '[data-tour="menu"]',
    title: 'Menú',
    content: 'Desde aquí ves tus cursos y te mueves entre las secciones de la plataforma.',
  },
  {
    name: 'classes',
    target: '[data-tour="classes"]',
    title: 'Clases',
    content:
      'Mira los videos de cada clase, márcalas como concluidas y avanza con el botón "Próxima Clase".',
  },
  {
    name: 'calendar',
    target: '[data-tour="calendar"]',
    title: 'Calendario',
    content: 'Revisa las fechas de las clases en vivo y los eventos del curso.',
  },
  {
    name: 'reports',
    target: '[data-tour="reports"]',
    title: 'Reportes',
    content: 'Consulta y descarga el último reporte publicado para tu curso.',
  },
  {
    name: 'forum',
    target: '[data-tour="forum"]',
    title: 'Foro',
    content: 'Haz preguntas y conversa con mentores y compañeros del curso.',
  },
  {
    name: 'tools',
    target: '[data-tour="tools"]',
    title: 'Herramientas',
    content:
      'Calculadoras y planillas para aplicar lo que aprendes: métricas, metas financieras, diagnóstico y más.',
  },
  {
    name: 'alliances',
    target: '[data-tour="alliances"]',
    title: 'Alianzas',
    content: 'Beneficios exclusivos con plataformas aliadas y tutoriales para aprovecharlos.',
  },
  {
    name: 'notifications',
    target: '[data-tour="notifications"]',
    title: 'Notificaciones',
    content: 'Te avisamos cuando hay clases nuevas, respuestas a tus comentarios y anuncios.',
  },
  {
    name: 'profile',
    target: '[data-tour="profile"]',
    title: 'Tu perfil',
    content: 'Edita tus datos personales o cierra sesión.',
  },
  {
    name: 'tutorial',
    target: '[data-tour="tutorial"]',
    title: 'Tutorial de la plataforma',
    content: 'Puedes repetir este recorrido o consultar las preguntas frecuentes cuando quieras.',
  },
];

const FAQS = [
  {
    question: '¿Cómo marco una clase como vista?',
    answer:
      'Dentro de la clase, toca el botón "Marcar como concluida". La clase queda marcada en el listado del costado y suma a tu progreso del curso.',
  },
  {
    question: '¿Cómo paso a la siguiente clase?',
    answer:
      'Usa el botón "Próxima Clase" que está junto al título, o elige cualquier clase desde el listado de módulos del costado.',
  },
  {
    question: '¿Dónde veo todos los módulos y clases del curso?',
    answer:
      'En la sección Clases, el listado de módulos aparece a la izquierda. En el celular, ábrelo con el botón de la barra lateral que está arriba de la clase.',
  },
  {
    question: '¿Cómo hago una pregunta sobre una clase?',
    answer:
      'Al final de cada clase está el foro de la clase. Escribe tu comentario y los mentores o compañeros te responderán. Puedes filtrar para ver solo las respuestas de mentores.',
  },
  {
    question: '¿Cómo me entero de clases nuevas o respuestas a mis comentarios?',
    answer:
      'En la campana de notificaciones de la barra superior. Ahí verás las clases nuevas, las respuestas a tus comentarios y los anuncios del curso.',
  },
  {
    question: '¿Dónde encuentro mi reporte?',
    answer:
      'En la sección Reportes puedes ver el último reporte publicado y descargarlo en PDF. Si todavía no hay uno disponible, lo verás ahí apenas se publique.',
  },
  {
    question: '¿Qué son las alianzas?',
    answer:
      'Son beneficios exclusivos con plataformas aliadas. En "Ver detalle" encuentras los requisitos, beneficios y tutoriales. Si un beneficio dice "En proceso", lo activaremos pronto.',
  },
  {
    question: '¿Cómo cambio mis datos personales?',
    answer: 'Toca tu foto o nombre en la esquina superior derecha y elige "Perfil".',
  },
  {
    question: '¿Puedo usar la plataforma desde el celular?',
    answer:
      'Sí. En el celular la barra superior muestra solo íconos; si no sabes qué es cada uno, repite el recorrido guiado desde "Tutorial de la plataforma".',
  },
  {
    question: 'El video de la clase no carga, ¿qué hago?',
    answer:
      'Revisa tu conexión a internet y recarga la página. Si sigue sin cargar, prueba con otro navegador (Chrome, Safari o Edge actualizados). Si el problema continúa, avísale al equipo de Inversapiens.',
  },
];

const isTargetVisible = (step: Step) => {
  const element = typeof step.target === 'string' ? document.querySelector(step.target) : step.target;
  return Boolean(element && (element as HTMLElement).offsetParent !== null);
};

const scrollTargetIntoView = (step?: Step) => {
  if (!step || typeof step.target !== 'string') {
    return;
  }
  document.querySelector(step.target)?.scrollIntoView({ block: 'nearest', inline: 'center' });
};

const PlatformTutorial = ({ iconOnly = false }: { iconOnly?: boolean }) => {
  const courseTheme = useCourseTheme();
  const { course } = useCourseCore();
  const { user } = useMeCore();

  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [faqOpen, setFaqOpen] = useState(false);
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  const [steps, setSteps] = useState<TourStep[]>([]);

  const seenKey = user?.id ? `${SEEN_STORAGE_KEY}:${user.id}` : null;

  const track = useCallback(
    (event: string, props: Record<string, unknown> = {}) => {
      if (process.env.REACT_APP_MIXPANEL_TOKEN) {
        mixpanel.track(event, { courseId: course?.id, courseName: course?.name, ...props });
      }
    },
    [course?.id, course?.name],
  );

  const startTour = useCallback(
    (trigger: 'auto' | 'manual') => {
      const visibleSteps = TOUR_STEPS.filter(isTargetVisible);
      if (!visibleSteps.length) {
        return;
      }

      scrollTargetIntoView(visibleSteps[0]);
      // Sin el punto que había que apretar: cada paso abre su explicación directo
      setSteps(visibleSteps.map((step) => ({ ...step, disableBeacon: true })));
      setStepIndex(0);
      setRun(true);
      track('Tutorial started', { trigger, totalSteps: visibleSteps.length });
    },
    [track],
  );

  useEffect(() => {
    if (!course || !seenKey || localStorage.getItem(seenKey)) {
      return;
    }

    const timeout = setTimeout(() => startTour('auto'), AUTO_START_DELAY_MS);
    return () => clearTimeout(timeout);
  }, [course, seenKey, startTour]);

  const finishTour = useCallback(() => {
    setRun(false);
    setStepIndex(0);
    if (seenKey) {
      localStorage.setItem(seenKey, 'true');
    }
  }, [seenKey]);

  const handleJoyrideCallback = useCallback(
    ({ action, index, status, type }: CallBackProps) => {
      if (status === STATUS.FINISHED) {
        track('Tutorial completed', { totalSteps: steps.length });
        finishTour();
        return;
      }

      if (status === STATUS.SKIPPED || action === ACTIONS.CLOSE) {
        track('Tutorial skipped', { step: index + 1, stepName: steps[index]?.name, totalSteps: steps.length });
        finishTour();
        return;
      }

      if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
        const nextIndex = index + (action === ACTIONS.PREV ? -1 : 1);
        scrollTargetIntoView(steps[nextIndex]);
        setStepIndex(nextIndex);
      }
    },
    [finishTour, steps, track],
  );

  const openFaq = () => {
    setMenuAnchor(null);
    setFaqOpen(true);
    track('FAQ opened');
  };

  const openTour = () => {
    setMenuAnchor(null);
    startTour('manual');
  };

  return (
    <>
      <Button
        data-tour="tutorial"
        className="h-40 mx-1 md:mx-2 min-w-40"
        onClick={(event) => setMenuAnchor(event.currentTarget)}
        size="large"
        aria-controls={menuAnchor ? 'platform-tutorial-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={menuAnchor ? 'true' : undefined}
      >
        <HelpOutlineIcon className={iconOnly ? '' : 'mr-0 lg:mr-2'} />
        {!iconOnly && <span className="hidden lg:block whitespace-nowrap">Tutorial de la plataforma</span>}
      </Button>

      <Menu
        id="platform-tutorial-menu"
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={openTour}>
          <ListItemIcon>
            <ExploreOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Recorrido guiado" />
        </MenuItem>
        <MenuItem onClick={openFaq}>
          <ListItemIcon>
            <QuizOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Preguntas frecuentes" />
        </MenuItem>
      </Menu>

      <Joyride
        steps={steps}
        stepIndex={stepIndex}
        run={run}
        callback={handleJoyrideCallback}
        continuous
        showProgress
        showSkipButton
        disableScrolling
        disableOverlayClose
        locale={{
          back: 'Anterior',
          close: 'Cerrar',
          last: 'Finalizar',
          next: 'Siguiente',
          skip: 'Saltar',
          nextLabelWithProgress: 'Siguiente ({step} de {steps})',
        }}
        styles={{
          options: {
            zIndex: 2000,
            primaryColor: courseTheme?.main ?? '#7c3aed',
            textColor: '#0f172a',
            width: 340,
          },
          tooltip: { borderRadius: 16 },
          tooltipTitle: { fontWeight: 800, fontSize: 17 },
          tooltipContent: { fontSize: 15, lineHeight: 1.5, padding: '12px 4px' },
          buttonNext: { borderRadius: 999, fontWeight: 700, padding: '8px 16px' },
          buttonBack: { color: '#475569' },
          buttonSkip: { color: '#94a3b8' },
        }}
      />

      <Dialog
        open={faqOpen}
        onClose={() => setFaqOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 4 } }}
      >
        <DialogTitle sx={{ fontWeight: 800, pb: 1 }}>Preguntas frecuentes</DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
          {FAQS.map((faq) => (
            <Accordion
              key={faq.question}
              disableGutters
              elevation={0}
              sx={{
                border: '1px solid #e2e8f0',
                borderRadius: 2,
                mb: 1,
                '&:before': { display: 'none' },
              }}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography sx={{ fontWeight: 700 }}>{faq.question}</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ pt: 0 }}>
                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                  {faq.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setFaqOpen(false)} variant="contained" color="secondary" sx={{ borderRadius: 999, px: 4 }}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PlatformTutorial;
